import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Info } from 'lucide-react';
import type { RootState } from '../../../../../store/redux/store/store';
import { Card, CardContent } from '../../../../../components/ui/Card';
import Input from '../../../../../components/ui/Input';
import Button from '../../../../../components/ui/Button';
import { Roles } from '../../../../../models/general';
import { getApiErrorMessage } from '../../../../../utils/apiErrors';
import {
  listCchiTierSchedules,
  createCchiTierSchedule,
  updateCchiTierSchedule,
  archiveCchiTierSchedule,
} from '../../../../../api/Payroll/cchi';
import type {
  CchiTierScheduleView,
  CchiTierScheduleInput,
  CchiPlanTier,
  CchiVisaCategory,
} from '../../../../../models/payroll';

const VISAS: CchiVisaCategory[] = ['SeniorExecutive', 'Professional', 'Skilled', 'SemiSkilled', 'Domestic'];
const TIERS: CchiPlanTier[] = ['ClassC', 'ClassB', 'ClassA', 'Vip'];

interface CchiTierScheduleTableProps {
  onError: (msg: string) => void;
}

export const CchiTierScheduleTable: React.FC<CchiTierScheduleTableProps> = ({ onError }) => {
  const { t } = useTranslation();
  const role = useSelector((s: RootState) => s.user.role);
  const isOperator = role === Roles.Operator || role === 'Operator';
  const [rows, setRows] = useState<CchiTierScheduleView[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CchiTierScheduleView | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listCchiTierSchedules();
      if (result.errors.length === 0) setRows(result.payload ?? []);
      else onError(getApiErrorMessage(result, t, t('common.error', 'An error occurred.')));
    } catch (e) {
      onError(getApiErrorMessage(e, t, t('common.error', 'An error occurred.')));
    } finally {
      setLoading(false);
    }
  }, [onError, t]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleArchive = async (id: number) => {
    if (!window.confirm(t('settings.payroll.cchi.tierSchedule.archive.confirmBody', 'Archive this tier row?'))) return;
    try {
      const result = await archiveCchiTierSchedule(id);
      if (result.errors.length === 0) await refresh();
      else onError(getApiErrorMessage(result, t, t('common.error', 'An error occurred.')));
    } catch (e) {
      onError(getApiErrorMessage(e, t, t('common.error', 'An error occurred.')));
    }
  };

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold">{t('settings.payroll.cchi.tierSchedule.title')}</h3>
            <p className="text-xs text-muted-foreground mt-1">{t('settings.payroll.cchi.tierSchedule.subtitle', 'CCHI visa-category × salary-band → expected plan tier.')}</p>
          </div>
          {isOperator ? (
            <Button onClick={() => setCreating(true)} iconName="Plus" size="sm">
              {t('settings.payroll.cchi.tierSchedule.addRow')}
            </Button>
          ) : (
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button type="button" aria-label={t('settings.payroll.cchi.tierSchedule.readOnlyHint')} className="text-muted-foreground hover:text-foreground">
                    <Info size={16} />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content className="bg-card border border-border rounded-md p-2 shadow-md text-xs max-w-xs z-50">
                    {t('settings.payroll.cchi.tierSchedule.readOnlyHint')}
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          )}
        </div>

        <div className="mb-3 rounded-md bg-muted/40 border border-border/60 px-3 py-2 text-xs text-muted-foreground">
          {t('settings.payroll.cchi.tierSchedule.explainer', 'Each row maps a visa category and monthly-salary band to the expected CCHI plan tier. It flags (does not block) when an employee’s saved policy tier doesn’t match the expected one. A blank max salary means ∞ — no upper limit, i.e. that tier applies to anyone earning the min salary and above.')}
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="text-start py-2 font-medium">{t('settings.payroll.cchi.tierSchedule.fields.visaCategory')}</th>
                  <th className="text-end py-2 font-medium">{t('settings.payroll.cchi.tierSchedule.fields.minSalary')}</th>
                  <th className="text-end py-2 font-medium">{t('settings.payroll.cchi.tierSchedule.fields.maxSalary')}</th>
                  <th className="text-start py-2 font-medium">{t('settings.payroll.cchi.tierSchedule.fields.expectedTier')}</th>
                  <th className="text-start py-2 font-medium">{t('settings.payroll.cchi.tierSchedule.fields.effectiveFrom')}</th>
                  <th className="text-start py-2 font-medium">{t('settings.payroll.cchi.tierSchedule.fields.effectiveTo')}</th>
                  {isOperator && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className={`border-b border-border last:border-0 ${r.isArchived ? 'opacity-60' : ''}`}>
                    <td className="py-2">{t(`profile.personal.visaCategoryOptions.${r.visaCategory}`, r.visaCategory)}</td>
                    <td className="py-2 text-end font-mono">{r.minMonthlySalary.toLocaleString('en-US')}</td>
                    <td className="py-2 text-end font-mono">{r.maxMonthlySalary != null ? r.maxMonthlySalary.toLocaleString('en-US') : '∞'}</td>
                    <td className="py-2">{t(`payroll.cchi.planTier.${r.expectedTier}`, r.expectedTier)}</td>
                    <td className="py-2">{new Date(r.effectiveFrom).toLocaleDateString()}</td>
                    <td className="py-2">{r.effectiveTo ? new Date(r.effectiveTo).toLocaleDateString() : '—'}</td>
                    {isOperator && (
                      <td className="py-2 text-end">
                        <button type="button" onClick={() => setEditing(r)} className="text-xs underline me-2">{t('Edit')}</button>
                        {!r.isArchived && (
                          <button type="button" onClick={() => handleArchive(r.id)} className="text-xs underline text-red-600">
                            {t('settings.payroll.cchi.tierSchedule.archive.confirmAction', 'Archive')}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="py-4 text-center text-muted-foreground">{t('common.noData')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {(editing || creating) && (
          <CchiTierEditDialog
            row={editing}
            onClose={() => { setEditing(null); setCreating(false); }}
            onSaved={async () => { setEditing(null); setCreating(false); await refresh(); }}
            onError={onError}
          />
        )}
      </CardContent>
    </Card>
  );
};

interface CchiTierEditDialogProps {
  row: CchiTierScheduleView | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onError: (msg: string) => void;
}

const CchiTierEditDialog: React.FC<CchiTierEditDialogProps> = ({ row, onClose, onSaved, onError }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<CchiTierScheduleInput>({
    id: row?.id,
    visaCategory: (row?.visaCategory as CchiVisaCategory) ?? 'Professional',
    minMonthlySalary: row?.minMonthlySalary ?? 0,
    maxMonthlySalary: row?.maxMonthlySalary ?? null,
    expectedTier: (row?.expectedTier as CchiPlanTier) ?? 'ClassC',
    effectiveFrom: row?.effectiveFrom ? row.effectiveFrom.slice(0, 10) : new Date().toISOString().slice(0, 10),
    effectiveTo: row?.effectiveTo ? row.effectiveTo.slice(0, 10) : null,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const result = row?.id
        ? await updateCchiTierSchedule(row.id, form)
        : await createCchiTierSchedule(form);
      if (result.errors.length === 0) await onSaved();
      else onError(getApiErrorMessage(result, t, t('common.error', 'An error occurred.')));
    } catch (e) {
      onError(getApiErrorMessage(e, t, t('common.error', 'An error occurred.')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/30 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md rounded-lg bg-card p-6 shadow-xl">
          <Dialog.Title className="text-base font-semibold mb-3">
            {row?.id ? t('settings.payroll.cchi.tierSchedule.edit.title') : t('settings.payroll.cchi.tierSchedule.add.title', 'Add CCHI tier row')}
          </Dialog.Title>
          <div className="space-y-3 text-xs">
            <Field label={t('settings.payroll.cchi.tierSchedule.fields.visaCategory')}>
              <select value={form.visaCategory} onChange={e => setForm({ ...form, visaCategory: e.target.value as CchiVisaCategory })} className="w-full rounded border bg-background px-3 py-2">
                {VISAS.map(v => <option key={v} value={v}>{t(`profile.personal.visaCategoryOptions.${v}`, v)}</option>)}
              </select>
            </Field>
            <Field label={t('settings.payroll.cchi.tierSchedule.fields.minSalary')}>
              <Input type="number" step="0.01" value={String(form.minMonthlySalary)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, minMonthlySalary: Number(e.target.value) || 0 })} />
            </Field>
            <Field label={t('settings.payroll.cchi.tierSchedule.fields.maxSalary')}>
              <Input
                type="number"
                step="0.01"
                placeholder={t('settings.payroll.cchi.tierSchedule.maxSalaryPlaceholder', 'Leave blank for ∞ (no upper limit)')}
                value={form.maxMonthlySalary != null ? String(form.maxMonthlySalary) : ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, maxMonthlySalary: e.target.value === '' ? null : Number(e.target.value) })}
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                {t('settings.payroll.cchi.tierSchedule.maxSalaryHint', 'Blank = ∞ (no upper limit). Leave “Effective to” blank to keep the row current.')}
              </p>
            </Field>
            <Field label={t('settings.payroll.cchi.tierSchedule.fields.expectedTier')}>
              <select value={form.expectedTier} onChange={e => setForm({ ...form, expectedTier: e.target.value as CchiPlanTier })} className="w-full rounded border bg-background px-3 py-2">
                {TIERS.map(tier => <option key={tier} value={tier}>{t(`payroll.cchi.planTier.${tier}`, tier)}</option>)}
              </select>
            </Field>
            <Field label={t('settings.payroll.cchi.tierSchedule.fields.effectiveFrom')}>
              <Input type="date" value={form.effectiveFrom} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, effectiveFrom: e.target.value })} />
            </Field>
            <Field label={t('settings.payroll.cchi.tierSchedule.fields.effectiveTo')}>
              <Input type="date" value={form.effectiveTo ?? ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, effectiveTo: e.target.value || null })} />
            </Field>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button onClick={onClose} variant="ghost" size="sm">{t('common.cancel')}</Button>
            <Button onClick={save} disabled={saving} size="sm">{saving ? t('common.saving') : t('common.save')}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium">{label}</label>
      {children}
    </div>
  );
}
